import { Switch } from '@thanx/uikit/switch'
import { withFormsy } from 'formsy-react'
import { PassDownProps } from 'formsy-react/dist/withFormsy'
import { useCallback, useMemo } from 'react'
import { Button } from 'react-bootstrap'

import EnhancedMultiSelect from 'components/Form/EnhancedMultiSelect'
import { useModifierSelectData } from 'hooks/useModifierSelectData'
import { buildTranslate } from 'locales'
import ModifierIdInput from 'scenes/RedeemManager/Builder/ConfigurationStep/OnlineRedemptionCard/ModifierIdInput'
import ModifierItems from 'scenes/RedeemManager/Builder/ConfigurationStep/OnlineRedemptionCard/ModifierItems'
import ModifierPopover from 'scenes/RedeemManager/Builder/ConfigurationStep/OnlineRedemptionCard/ModifierPopover'
import { useFormModel } from 'scenes/RedeemManager/Builder/FormModel'
import { buildModifierOptionName } from 'scenes/RedeemManager/helpers'

import type { Option } from 'baseui/select'
import type { Fields as Modifier } from 'models/Modifier'

type ModifierSelectProps = PassDownProps<string[]> & {
  label: string
  helpText?: string
  secondaryHelpText?: string
  fallbackHelpText?: string
  className?: string
  productIds?: string[]
  toggleVisibility?: (visible: boolean) => void
  shouldDisplayPopover?: boolean
}

const t = buildTranslate('redeem_manager.modifier_select')

const ModifierSelect = (props: ModifierSelectProps) => {
  const {
    name,
    value: selectedIds = [],
    productIds = [],
    setValue,
    label,
    secondaryHelpText,
    fallbackHelpText,
    toggleVisibility,
    helpText,
    className = '',
    shouldDisplayPopover = false,
  } = props

  const { setModel, model } = useFormModel()

  const {
    modifiersData: { modifiers, pagination, isLoading },
    searchState: {
      searchTerm,
      setSearchTerm,
      setPage,
      isFocused,
      setIsFocused,
    },
    selection: {
      selectedModifiers,
      setSelectedModifiers,
      isModifierSelectedBy,
    },
    uiState: { shouldDisplayIdInput },
  } = useModifierSelectData(selectedIds, productIds, setValue)

  const getCustomLabel = useCallback((option?: Option) => {
    const name = option?.name || option?.label || ''
    const [boldText, regularText] = name.split(' - ', 2)

    return (
      <>
        <strong>{boldText}</strong>
        {regularText && ` - ${regularText}`}
      </>
    )
  }, [])

  const resetValues = () => {
    setModel({ ...model, [name]: [] })
    setSearchTerm('')
    setPage(1)
  }

  const options: Option[] = useMemo(() => {
    return modifiers
      .filter(
        modifier =>
          modifier.option &&
          modifier.items?.length &&
          modifier.items.some(item => productIds.includes(item.chain_uid)) &&
          modifier.option.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map(modifier => ({
        ...modifier.option,
        name: buildModifierOptionName(modifier),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [modifiers, productIds, searchTerm])

  const selectedModifierOptions = selectedModifiers.map(mod => mod.option)

  const deleteItem = (uid: string) => {
    const newSelectedIds = selectedIds.filter(id => id !== uid)
    setValue(newSelectedIds)
    setSelectedModifiers(prev =>
      prev.filter(modifier => !isModifierSelectedBy([uid])(modifier))
    )
  }

  const handleChange = (selections: Option[]) => {
    const newSelectedIds = selections.map(option => option.chain_uid)
    setValue(newSelectedIds)

    const newSelectedModifiers = selections
      .map(option => modifiers.find(isModifierSelectedBy([option.chain_uid])))
      .filter(Boolean) as Modifier[]

    setSelectedModifiers(newSelectedModifiers)
  }

  const handleFocusAndBlur = (focusState: boolean) => () =>
    setIsFocused(focusState)

  const toggleModifierVisibility = () => {
    toggleVisibility?.(false)
    resetValues()
  }

  const getPlaceholder = () => (isFocused ? t('search') : t('select_modifier'))

  if (shouldDisplayIdInput) {
    return (
      <ModifierIdInput
        label={label}
        name={name}
        helpText={fallbackHelpText || helpText}
        secondaryHelpText={secondaryHelpText}
        className={className}
        selectedIds={selectedIds}
        setValue={setValue}
        toggleVisibility={toggleModifierVisibility}
        shouldDisplayPopover={shouldDisplayPopover}
      />
    )
  }

  return (
    <div className={`${className} mt-l`}>
      <div className="d-flex flex-row justify-content-between">
        <div className="d-flex flex-row">
          <p className="body-text-14 bold mb-0">{label}</p>
          <Switch condition={shouldDisplayPopover}>
            <ModifierPopover />
          </Switch>
        </div>
        <Switch condition={Boolean(toggleVisibility)}>
          <Button
            bsStyle="link"
            type="button"
            onClick={toggleModifierVisibility}
            className="p-0 m-0"
          >
            {t('wrapper.hide')}
          </Button>
        </Switch>
      </div>

      <Switch condition={Boolean(helpText)}>
        <p className="mt-xs body-text-4 grey-70">{helpText}</p>
      </Switch>

      <div className="d-flex flex-row mt-xs">
        <EnhancedMultiSelect
          pagination={pagination}
          options={options}
          onSearch={setSearchTerm}
          onPageChange={setPage}
          isLoading={isLoading}
          placeholder={getPlaceholder()}
          maxDropdownHeight="400px"
          labelKey="name"
          valueKey="chain_uid"
          valueName="modifier"
          isFocused={isFocused}
          disabled={!productIds.length}
          value={selectedModifierOptions}
          onFocus={handleFocusAndBlur(true)}
          onBlur={handleFocusAndBlur(false)}
          onChange={handleChange}
          getCustomLabel={getCustomLabel}
        />
      </div>

      <div className="mt-s">
        <ModifierItems
          deleteItem={deleteItem}
          orderedSelectedModifiers={selectedModifiers}
          productIds={productIds}
        />
      </div>
    </div>
  )
}

export default withFormsy(ModifierSelect)
